import {
  alpha,
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  useTheme,
  Button,
  Table,
  TableContainer,
  TableRow,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { getNodeColorByGroup, getNodeConfig } from 'api/workflows-steps/formSchema';
import { FONT_MONO } from 'themes/tokens';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CloseButton from '../../../components/button/CloseButton.tsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNodeOutputStream } from '../../../api/sse/useNodeOutputStream';
import {
  getNodeOutputData,
  downloadNodeOutputDataFile,
  getNodeOutputDataColumns,
} from '../../../api/workflows/requests.ts';
import LoadingDialog from '../../../components/dialog/LoadingDialog.tsx';
import { NodeOutputData, NodeOutputDetailsResponse, QueryKey, WorkflowNode } from '../../../api/model.ts';
import { StyledSelect, StyledTableCell } from './Canvas.styles.tsx';
import { FaDownload } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { TableVirtuoso } from 'react-virtuoso';
import { ArrowDropDown, ArrowDropUp, Search } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import ResizableTableCell from 'components/table/ResizableTableCell.tsx';
import { useInfiniteListFill } from 'hooks/useInfiniteListFill';
import Loader from 'components/Loader.tsx';
import { useDebounce } from 'hooks/useDebounce.ts';
import NodeOutputCellDialog, { CELL_PREVIEW_SIZE, CellData } from './NodeOutputCellDialog.tsx';
import { ExecutionNodeStatusEnum } from 'types.ts';

const PAGE_SIZE = 50;

type ColumnSearchFieldProps = {
  columns: string[];
  searchColumn?: string;
  value?: string;
  handleSearchChange: (columnName: string | undefined, value: string) => void;
  handleResetSearch: () => void;
};

const ColumnSearchField = (props: ColumnSearchFieldProps) => {
  const [searchText, setSearchText] = useState<string>(props.value ?? '');

  const debouncedSearchText = useDebounce(searchText, 3000);

  useEffect(() => {
    props.handleSearchChange(props.searchColumn, debouncedSearchText);
  }, [debouncedSearchText]);

  const handleSearchColumnChange = (event: SelectChangeEvent) => {
    props.handleSearchChange(event.target.value, searchText);
    setSearchText('');
  };

  const handleResetSearch = () => {
    props.handleResetSearch();
    setSearchText('');
  };

  return (
    <FormControl>
      <div>
        <InputLabel id="column-select-label">Search Column</InputLabel>
        <Select
          value={props.searchColumn ?? ''}
          labelId="column-select-label"
          className="min-w-40 max-w-40 overflow-hidden text-ellipsis rounded-s-md rounded-e-none bg-gray-100/50 dark:bg-neutral-800/50 "
          onChange={handleSearchColumnChange}
        >
          {props.columns.map((col) => (
            <MenuItem key={col} value={col}>
              {col}
            </MenuItem>
          ))}
        </Select>
        <TextField
          disabled={!props.searchColumn}
          variant="outlined"
          className="min-w-60 max-w-60 overflow-hidden text-ellipsis"
          placeholder="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            classes: { root: 'rounded-e-md rounded-s-none' },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  className={searchText ? '' : 'invisible'}
                  onClick={handleResetSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </div>
    </FormControl>
  );
};

type NodeOutputDetailsProps = {
  node: WorkflowNode | null;
  workflowExecutionId?: string | null;
  nodeStatus?: ExecutionNodeStatusEnum;
  onValueSelect?: (value: string) => void;
  onClose?: () => void;
};

const NodeOutputDetails = ({
  node,
  workflowExecutionId,
  nodeStatus,
  onValueSelect,
  onClose,
}: NodeOutputDetailsProps) => {
  const [sortColumn, setSortColumn] = useState('giq_index');
  const [sortDirection, setSortDirection] = useState<'' | '-'>('');
  const [searchText, setSearchText] = useState<string | undefined>();
  const [searchColumn, setSearchColumn] = useState<string | undefined>();
  const [cellData, setCellData] = useState<CellData>();
  const [isDownloadCancelled, setIsDownloadCancelled] = useState(false);
  const downloadAbortController = useRef(new AbortController());
  const isNodeRunning = nodeStatus === ExecutionNodeStatusEnum.RUNNING;

  const { data, fetchNextPage, hasNextPage, isFetching, isError } = useInfiniteQuery({
    queryKey: [
      QueryKey.NodeOutputData,
      workflowExecutionId,
      node?.id,
      sortColumn,
      sortDirection,
      searchText,
      nodeStatus,
    ],
    queryFn: ({ pageParam }) =>
      getNodeOutputData({
        workflowExecutionId: workflowExecutionId ?? '',
        nodeId: node?.id ?? '',
        pageSize: PAGE_SIZE,
        pageParam,
        sortColumn,
        sortDirection,
        searchColumn,
        searchText,
        previewSize: CELL_PREVIEW_SIZE,
      }),
    enabled: !!node,
    getNextPageParam: (lastPage: NodeOutputDetailsResponse, pages) => {
      return lastPage.data?.length && lastPage.data?.length >= PAGE_SIZE ? pages.length + 1 : null;
    },
    initialPageParam: 1,
  });

  const queryClient = useQueryClient();
  useNodeOutputStream(workflowExecutionId, node?.id, isNodeRunning && !!node, {
    onRows: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.NodeOutputData, workflowExecutionId, node?.id],
      });
    },
    onResync: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.NodeOutputData, workflowExecutionId, node?.id],
      });
    },
  });

  const { data: columnNames } = useQuery({
    queryKey: [QueryKey.NodeOutputData, workflowExecutionId, node?.id, 'columns'],
    queryFn: () =>
      getNodeOutputDataColumns({
        workflowExecutionId: workflowExecutionId ?? '',
        nodeId: node?.id ?? '',
      }),
    enabled: !!node,
  });

  const keys = useMemo<string[]>(() => columnNames ?? [], [columnNames?.join(',')]);

  const totalFetchedData = useMemo<NodeOutputData[]>(
    () => data?.pages?.map((page: NodeOutputDetailsResponse) => page.data).flat() ?? [],
    [data],
  );

  const totalCount = data?.pages?.length ? data?.pages[0].total : 0;

  const { handleScrollerRef, handleTotalListHeightChanged, handleEndReached } = useInfiniteListFill({
    itemCount: totalFetchedData.length,
    hasMore: hasNextPage,
    isLoading: isFetching,
    loadMore: fetchNextPage,
  });

  const theme = useTheme();

  const nodeConfig = node ? getNodeConfig(node.type) : undefined;
  const nodeColor = nodeConfig ? getNodeColorByGroup(nodeConfig.group) : theme.palette.primary.main;

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({ giq_index: 30 });

  const [resultDownloadProgress, setResultDownloadProgress] = useState(0);

  useEffect(() => {
    if (totalFetchedData && keys && !selectedColumns.length) {
      setSelectedColumns(keys);
    }
  }, [keys, selectedColumns, totalFetchedData]);

  const handleSelectedColumnsChange = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event;
    setSelectedColumns(value as string[]);
  };

  const { isFetching: isNodeResultsLoading, refetch: fetchNodeResults } = useQuery({
    queryKey: [node?.id, workflowExecutionId, 'results'],
    queryFn: () =>
      downloadNodeOutputDataFile({
        workflowExecutionId: workflowExecutionId ?? '',
        nodeId: node?.id ?? '',
        setProgress: setResultDownloadProgress,
        sortColumn,
        sortDirection,
        searchColumn,
        searchText,
        abortController: downloadAbortController.current,
      }),
    enabled: false,
  });

  const handleDownload = async () => {
    downloadAbortController.current = new AbortController();
    setIsDownloadCancelled(false);
    setResultDownloadProgress(0);
    try {
      await fetchNodeResults();
      setResultDownloadProgress(100);
    } catch {
      toast.error('Error downloading file');
    } finally {
      setResultDownloadProgress(0);
    }
  };

  const copyToClipboard = (event: React.MouseEvent<HTMLTableCellElement>, content: string) => {
    event.preventDefault();
    void navigator.clipboard.writeText(content);
  };

  const handleSortChange = (key: string) => {
    if (key === sortColumn) {
      setSortDirection((prev) => (prev === '' ? '-' : ''));
    } else {
      setSortDirection('');
      setSortColumn(key);
    }
  };

  const handleSearchChange = (columnName: string | undefined, value: string) => {
    setSearchColumn(columnName);
    setSearchText(value);
  };

  const handleResetSearch = () => {
    setSearchColumn(undefined);
    setSearchText(undefined);
  };

  const handleColResize = (newWidth: number, key: string) => setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));

  const cancelDownload = () => {
    downloadAbortController.current.abort();
    setIsDownloadCancelled(true);
  };

  useEffect(() => {
    if (isError) {
      toast.error('Node output data could not be loaded');
      onClose?.();
    }
  }, [isError, onClose]);

  return (
    <>
      <div
        className="w-full h-full bg-gray-100 top-0 bottom-0 px-8 flex flex-col gap-1"
        style={{
          background: theme.palette.background.default,
        }}
      >
        <div className="flex flex-row justify-between items-center pt-4 pb-2">
          <div className="flex flex-row gap-4 items-center">
            <div className="flex items-center gap-3 min-w-0">
              {nodeConfig?.icon && (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: nodeColor,
                    color: theme.palette.getContrastText(nodeColor),
                    boxShadow: `0 4px 14px ${alpha(nodeColor, 0.5)}`,
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {nodeConfig.icon}
                </Box>
              )}
              <span className="text-xl font-bold truncate">{node?.name}</span>
            </div>
            <StyledSelect
              multiple
              value={selectedColumns}
              onChange={handleSelectedColumnsChange}
              renderValue={() => 'Displayed columns'}
            >
              {keys.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={selectedColumns.includes(name)} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </StyledSelect>
            {totalFetchedData && (
              <Box
                component="span"
                sx={(t) => ({
                  fontFamily: FONT_MONO,
                  fontSize: '0.78rem',
                  color: 'text.secondary',
                  px: 1.25,
                  py: 0.75,
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  border: `1px solid ${t.palette.divider}`,
                  backgroundColor: alpha(t.palette.primary.main, 0.06),
                })}
              >
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  {totalFetchedData?.length ?? 0}
                </Box>
                {' / '}
                {totalCount}
                <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                  rows
                </Box>
              </Box>
            )}
            <ColumnSearchField
              columns={keys}
              searchColumn={searchColumn}
              value={searchText}
              handleSearchChange={handleSearchChange}
              handleResetSearch={handleResetSearch}
            />
          </div>
          <div className="flex flex-row gap-2 items-center">
            {isNodeRunning && <CircularProgress className="mr-0" size={32} thickness={5} color="secondary" />}
            <Button
              variant="contained"
              color="primary"
              startIcon={<FaDownload />}
              onClick={() => void handleDownload()}
            >
              {searchColumn && searchText ? 'Download Filtered' : 'Download All'}
            </Button>
            <CloseButton onClose={() => onClose?.()} />
          </div>
        </div>
        <TableContainer
          className={'mb-5 h-full w-full flex-1' + (isNodeRunning ? ' animate-[softPulse_2s_infinite]' : '')}
          component={Paper}
          sx={(t) => ({ borderRadius: '12px', border: `1px solid ${t.palette.divider}`, backgroundImage: 'none' })}
        >
          <TableVirtuoso
            data={totalFetchedData}
            fixedFooterContent={() =>
              isFetching ? (
                <tr>
                  <td colSpan={selectedColumns.length}>
                    <Loader />
                  </td>
                </tr>
              ) : null
            }
            fixedHeaderContent={() => (
              <TableRow style={{ background: theme.palette.background.default }}>
                {selectedColumns.map((key) => (
                  <ResizableTableCell
                    key={key}
                    width={columnWidths[key] || 180}
                    onResize={(newWidth: number) => handleColResize(newWidth, key)}
                    onClick={() => handleSortChange(key)}
                    sx={(t) => ({
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      letterSpacing: '0.03em',
                      color: searchColumn === key ? t.palette.primary.light : t.palette.text.secondary,
                      borderBottom: `1px solid ${t.palette.divider}`,
                      backgroundColor: searchColumn === key ? alpha(t.palette.primary.main, 0.18) : 'transparent',
                      transition: t.transitions.create(['background-color', 'color']),
                      '&:hover': { backgroundColor: alpha(t.palette.primary.main, 0.1) },
                    })}
                  >
                    <div className="flex justify-between items-center gap-1">
                      <span className="truncate min-w-0">{key}</span>
                      <span className="flex items-center shrink-0">
                        {sortColumn === key &&
                          (sortDirection === '' ? (
                            <ArrowDropUp fontSize="small" />
                          ) : (
                            <ArrowDropDown fontSize="small" />
                          ))}
                      </span>
                    </div>
                  </ResizableTableCell>
                ))}
              </TableRow>
            )}
            itemContent={(index, item: NodeOutputData) => (
              <>
                {selectedColumns.map((key, colIndex) => {
                  const content: string = typeof item[key] === 'string' ? item[key] : JSON.stringify(item[key]);
                  return (
                    <StyledTableCell
                      key={`${index}-${colIndex}`}
                      sx={
                        searchColumn === key ? { backgroundColor: alpha(theme.palette.primary.main, 0.07) } : undefined
                      }
                      style={{ width: columnWidths[key] || 0, maxWidth: columnWidths[key] || 0 }}
                      onContextMenu={(event) => copyToClipboard(event, content)}
                      onClick={() =>
                        setCellData({
                          index: totalFetchedData[index].giq_index as number,
                          column: key,
                          content,
                        })
                      }
                    >
                      {content}
                    </StyledTableCell>
                  );
                })}
              </>
            )}
            endReached={handleEndReached}
            scrollerRef={handleScrollerRef}
            totalListHeightChanged={handleTotalListHeightChanged}
            components={{
              Table: (props) => <Table {...props} style={{ width: '100%', tableLayout: 'fixed' }} />,
              // eslint-disable-next-line react/prop-types
              TableHead: (props) => <thead {...props} style={{ ...props.style, zIndex: 20 }} />,
            }}
            style={{ height: '100%' }}
          />
        </TableContainer>
      </div>
      <NodeOutputCellDialog
        workflowExecutionId={workflowExecutionId ?? ''}
        nodeId={node?.id ?? ''}
        cellData={cellData}
        onClose={() => setCellData(undefined)}
        onValueSelect={onValueSelect}
      />
      <LoadingDialog
        isVisible={isNodeResultsLoading && !isDownloadCancelled}
        title="Downloading results"
        progress={resultDownloadProgress}
        onCancel={cancelDownload}
      />
    </>
  );
};

export default NodeOutputDetails;
